/**
 * Group List API Functions
 * Handles group listing and querying functionality
 */

import { getAuthConfigFromStore, getAuthErrorMessage } from '../../auth/authUtils';
import { getApiGateway } from '../../api/endpointUtils';
import type {
  GetGroupListParams,
  GetGroupListResponse
} from '../types';
import { GET_GROUP_LIST_ERROR_MESSAGES } from '../types';

/**
 * Get list of groups for the authenticated user
 * Retrieves groups with pagination, ordering, and search functionality
 * 
 * @param params - Group list query parameters (email and userToken from AuthStore if not provided)
 * @returns Promise with group list response
 */
export async function getGroupList(params: Omit<GetGroupListParams, 'email' | 'userToken'> & {
  email?: string;
  userToken?: string;
} = {}): Promise<GetGroupListResponse> {
  // Get auth config from store
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error(getAuthErrorMessage());
  }

  try {
    // Get API Gateway URL from endpoint store
    const API_GATEWAY = getApiGateway();
    
    // Merge parameters with auth config
    const requestParams: GetGroupListParams = {
      email: authConfig.userEmail,
      userToken: authConfig.userToken,
      tokenType: 'web',
      ordering: 0, // Default ordering
      ...params
    };

    // Build query string
    const queryParams = new URLSearchParams();
    queryParams.append('email', requestParams.email);
    queryParams.append('userToken', requestParams.userToken);
    queryParams.append('tokenType', requestParams.tokenType || 'web');
    queryParams.append('ordering', (requestParams.ordering || 0).toString());
    
    if (requestParams.keyword !== undefined) {
      queryParams.append('keyword', requestParams.keyword);
    }
    if (requestParams.startIndex && requestParams.startIndex >= 1) {
      queryParams.append('startIndex', requestParams.startIndex.toString());
    }
    if (requestParams.endIndex && requestParams.endIndex >= 1) {
      queryParams.append('endIndex', requestParams.endIndex.toString());
    }

    // Make API request
    const response = await fetch(`${API_GATEWAY}/DashCam/GetGroupList?${queryParams.toString()}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: GetGroupListResponse = await response.json();

    // Handle API error responses
    if (data.resultcode !== 'BC_ERR_OK') {
      const errorMessage = GET_GROUP_LIST_ERROR_MESSAGES[data.resultcode as keyof typeof GET_GROUP_LIST_ERROR_MESSAGES] 
        || data.message 
        || 'Unknown error occurred';
      throw new Error(errorMessage);
    }
    
    return data;

  } catch (error) {
    throw error;
  }
}